import React from "react";
import { motion } from "framer-motion";

const RevenueLeakageSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[900px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col"
        >
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-[34px] md:text-[44px] lg:text-[52px] font-extrabold text-slate-900 mb-7 leading-[1.05] tracking-tight text-center"
          >
            The cost of doing nothing is{" "}
            <span className="text-blue-600">35%–50%+ of revenue</span>{" "}
            for most practices.
          </motion.h2>

          <article>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.45 }}
              className="text-[22px] lg:text-[24px] text-slate-800 leading-[1.55] font-medium mb-6"
            >
              Most dental office owners know there's a problem. They assume it's worth{" "}
              <span className="font-semibold text-slate-900">10–15% in lost profit margin</span>. What they don't see is that the problem is{" "}
              <span className="italic font-semibold text-slate-900">far bigger</span> than they think.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.45 }}
              className="text-[22px] lg:text-[24px] text-slate-800 leading-[1.55] font-medium"
            >
              A <span className="font-bold text-slate-900">$100K/month practice</span> isn't really a $100K practice. It's a{" "}
              <span className="relative inline-block font-extrabold text-blue-700">
                $150K–$190K practice
                <span className="absolute left-0 right-0 bottom-0 h-[8px] bg-blue-200/70 -z-10 rounded" />
              </span>{" "}
              compressed by <span className="italic font-semibold text-slate-900">operational gravity</span> — missed calls, unworked denials, lapsed patients, and missed opportunities for growth.
            </motion.p>
          </article>

          {/* Editorial pull-quote */}
          <motion.blockquote
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="relative mt-10"
          >
            <div
              className="absolute -left-2 -top-10 text-[110px] font-serif text-blue-200 leading-none select-none pointer-events-none"
              aria-hidden
            >
              &ldquo;
            </div>
            <div className="relative pl-8 border-l-[4px] border-blue-600 rounded-[1px]">
              <p className="text-[22px] lg:text-[24px] text-slate-900 leading-[1.5] font-semibold italic">
                Doing nothing isn't the safe choice. It's the{" "}
                <span className="not-italic font-extrabold text-blue-700">
                  most expensive line item in the practice
                </span>{" "}
                — you just never get an invoice for it.
              </p>
            </div>
          </motion.blockquote>
        </motion.div>
      </div>
    </div>
  );
};

export default RevenueLeakageSlide;
